import { Component, OnInit, computed, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import {
  AuthStore,
  ErrorAlertComponent,
  FeatureHeaderComponent,
  PERMISSIONS,
  SubmitButtonComponent,
  TextInputComponent,
} from '@sandbox-platform/shared-ui';

import { buildReferralSourceChannelForm } from './referral-source-channel-detail.form';
import { injectReferralSourceChannelDetailStore } from './+state/referral-source-channel-detail.store';
import { ReferralSourceChannelDTO } from '../../../types/referral-source-channel.types';

@Component({
  standalone: true,
  imports: [
    ReactiveFormsModule,
    ErrorAlertComponent,
    FeatureHeaderComponent,
    SubmitButtonComponent,
    TextInputComponent,
  ],
  selector: 'lib-referral-source-channel-detail',
  templateUrl: './referral-source-channel-detail.component.html',
})
export class ReferralSourceChannelDetailComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly authStore = inject(AuthStore);
  protected readonly store = injectReferralSourceChannelDetailStore();

  protected readonly form = buildReferralSourceChannelForm(this.fb);
  protected readonly id = this.route.snapshot.paramMap.get('id');

  protected readonly title = computed(() =>
    this.store.isCreateMode()
      ? 'Create Source Channel'
      : 'Edit Source Channel'
  );

  protected readonly canSave = computed(() =>
    this.authStore.hasExpertPermission(
      this.store.isCreateMode()
        ? PERMISSIONS.EXPERT.REFERRAL_SOURCE_CHANNEL_CREATE
        : PERMISSIONS.EXPERT.REFERRAL_SOURCE_CHANNEL_UPDATE
    )
  );

  async ngOnInit(): Promise<void> {
    if (this.id) {
      const sourceChannel = await this.store.load(this.id);
      if (sourceChannel) {
        this.form.patchValue(sourceChannel);
      }
      return;
    }

    this.store.initializeForCreate();
    this.form.reset({
      label: '',
      code: '',
      requires_additional_info: false,
      display_order: 0,
      is_active: true,
    });
  }

  protected async onSubmit(): Promise<void> {
    if (this.form.invalid || !this.canSave()) {
      this.form.markAllAsTouched();
      return;
    }

    const result = this.store.isCreateMode()
      ? await this.createSourceChannel()
      : await this.updateSourceChannel();

    if (result) {
      this.router.navigate(['/referral-source-channels']);
    }
  }

  private async createSourceChannel(): Promise<ReferralSourceChannelDTO | null> {
    const value = this.form.getRawValue();

    return await this.store.create({
      code: value.code ?? '',
      label: value.label ?? '',
      requires_additional_info: Boolean(value.requires_additional_info),
      display_order: Number(value.display_order ?? 0),
      is_active: value.is_active ?? true,
    });
  }

  private async updateSourceChannel(): Promise<ReferralSourceChannelDTO | null> {
    if (!this.id) {
      return null;
    }

    const value = this.form.getRawValue();

    return await this.store.update(this.id, {
      label: value.label ?? '',
      requires_additional_info: Boolean(value.requires_additional_info),
      display_order: Number(value.display_order ?? 0),
      is_active: value.is_active ?? true,
    });
  }

  protected onCancel(): void {
    this.router.navigate(['/referral-source-channels']);
  }
}
